import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, ShieldCheck, Trash2 } from 'lucide-react'
import { useGuildStore } from '@/store/guilds'
import { guildApi } from '@/lib/guildApi'
import { describeError } from '@/lib/auth'
import { cx } from '@/lib/cx'
import { AutomodActionType, AutomodTrigger, ChannelType, type AutomodAction, type AutomodRule, type AutomodRuleInput, type Guild } from '@/types/api'
import { Field } from '@/pages/AuthPage'
import auth from '@/pages/AuthPage.module.css'
import layer from '../settings/SettingsLayer.module.css'
import gs from './GuildSettingsLayer.module.css'
import styles from './AutomodSection.module.css'
import { Select } from '../Select'

const TRIGGERS: { value: number; label: string; hint: string }[] = [
  { value: AutomodTrigger.Keyword, label: 'Ключевые слова и ссылки', hint: 'до 6 правил: слова со звёздочками, регулярные выражения, фильтр ссылок и приглашений' },
  { value: AutomodTrigger.KeywordPreset, label: 'Встроенные списки', hint: 'нецензурная лексика, сексуальный контент, оскорбления по признаку' },
  { value: AutomodTrigger.MentionSpam, label: 'Лимит упоминаний', hint: 'слишком много упомянутых участников и ролей в одном сообщении' },
  { value: AutomodTrigger.Spam, label: 'Антиспам', hint: 'больше 5 сообщений за 5 секунд или один и тот же текст 3 раза за минуту' },
]

const PRESETS: { value: number; label: string }[] = [
  { value: 1, label: 'Нецензурная лексика' },
  { value: 2, label: 'Сексуальный контент' },
  { value: 3, label: 'Оскорбления по признаку' },
]

const LINK_PATTERN = 'https?://'
const INVITE_PATTERN = '(klichat\\.ru/invite/|discord\\.gg/|discord\\.com/invite/)'

const TIMEOUTS: { value: number; label: string }[] = [
  { value: 60, label: '1 минута' },
  { value: 300, label: '5 минут' },
  { value: 600, label: '10 минут' },
  { value: 3600, label: '1 час' },
  { value: 86400, label: '1 день' },
  { value: 604800, label: '1 неделя' },
]

interface Draft {
  name: string
  trigger: number
  keywords: string
  regex: string
  allow: string
  links: boolean
  invites: boolean
  presets: number[]
  mentionLimit: number
  block: boolean
  customMessage: string
  alert: boolean
  alertChannel: string
  timeout: boolean
  timeoutSeconds: number
  exemptRoles: string[]
  exemptChannels: string[]
  enabled: boolean
}

const emptyDraft: Draft = {
  name: '', trigger: AutomodTrigger.Keyword, keywords: '', regex: '', allow: '', links: false, invites: false, presets: [1], mentionLimit: 10,
  block: true, customMessage: '', alert: false, alertChannel: '', timeout: false, timeoutSeconds: 600, exemptRoles: [], exemptChannels: [], enabled: true,
}

const lines = (s: string) => s.split(/[\n,]/).map((x) => x.trim()).filter(Boolean)

function toDraft(r: AutomodRule): Draft {
  const regex = r.trigger_metadata.regex_patterns.filter((p) => p !== LINK_PATTERN && p !== INVITE_PATTERN)
  const block = r.actions.find((a) => a.type === AutomodActionType.BlockMessage)
  const alert = r.actions.find((a) => a.type === AutomodActionType.SendAlert)
  const timeout = r.actions.find((a) => a.type === AutomodActionType.Timeout)
  return {
    name: r.name, trigger: r.trigger_type, keywords: r.trigger_metadata.keyword_filter.join('\n'), regex: regex.join('\n'), allow: r.trigger_metadata.allow_list.join('\n'),
    links: r.trigger_metadata.regex_patterns.includes(LINK_PATTERN), invites: r.trigger_metadata.regex_patterns.includes(INVITE_PATTERN),
    presets: r.trigger_metadata.presets.length ? r.trigger_metadata.presets : [1], mentionLimit: r.trigger_metadata.mention_total_limit ?? 10,
    block: Boolean(block), customMessage: block?.metadata.custom_message ?? '', alert: Boolean(alert), alertChannel: alert?.metadata.channel_id ?? '',
    timeout: Boolean(timeout), timeoutSeconds: timeout?.metadata.duration_seconds ?? 600, exemptRoles: r.exempt_roles, exemptChannels: r.exempt_channels, enabled: r.enabled,
  }
}

function toInput(d: Draft): AutomodRuleInput {
  const actions: AutomodAction[] = []
  if (d.block) actions.push({ type: AutomodActionType.BlockMessage, metadata: d.customMessage.trim() ? { custom_message: d.customMessage.trim() } : {} })
  if (d.alert && d.alertChannel) actions.push({ type: AutomodActionType.SendAlert, metadata: { channel_id: d.alertChannel } })
  if (d.timeout && d.trigger !== AutomodTrigger.Spam) actions.push({ type: AutomodActionType.Timeout, metadata: { duration_seconds: d.timeoutSeconds } })
  const regex = lines(d.regex)
  if (d.trigger === AutomodTrigger.Keyword) {
    if (d.links) regex.push(LINK_PATTERN)
    if (d.invites) regex.push(INVITE_PATTERN)
  }
  return {
    name: d.name.trim(),
    event_type: 1,
    trigger_type: d.trigger,
    trigger_metadata: {
      keyword_filter: d.trigger === AutomodTrigger.Keyword ? lines(d.keywords) : [],
      regex_patterns: d.trigger === AutomodTrigger.Keyword ? regex : [],
      presets: d.trigger === AutomodTrigger.KeywordPreset ? d.presets : [],
      allow_list: d.trigger === AutomodTrigger.Keyword || d.trigger === AutomodTrigger.KeywordPreset ? lines(d.allow) : [],
      ...(d.trigger === AutomodTrigger.MentionSpam ? { mention_total_limit: d.mentionLimit } : {}),
    },
    actions,
    enabled: d.enabled,
    exempt_roles: d.exemptRoles,
    exempt_channels: d.exemptChannels,
  }
}

function triggerLabel(t: number): string {
  return TRIGGERS.find((x) => x.value === t)?.label ?? `тип ${t}`
}

function actionsLabel(r: AutomodRule): string {
  return r.actions
    .map((a) => (a.type === AutomodActionType.BlockMessage ? 'блокировать' : a.type === AutomodActionType.SendAlert ? 'оповестить' : 'таймаут'))
    .join(', ')
}

/** Настройки сервера → «Автомод»: список правил и редактор (MANAGE_GUILD). */
export function AutomodSection({ guild }: { guild: Guild }) {
  const qc = useQueryClient()
  const rules = useQuery({ queryKey: ['automod', guild.id], queryFn: () => guildApi.automodRules(guild.id) })
  const [editing, setEditing] = useState<'new' | string | null>(null)
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['automod', guild.id] })
  const toggle = useMutation({ mutationFn: (r: AutomodRule) => guildApi.updateAutomodRule(guild.id, r.id, { enabled: !r.enabled }), onSuccess: invalidate })
  const remove = useMutation({ mutationFn: (id: string) => guildApi.deleteAutomodRule(guild.id, id), onSuccess: invalidate })
  const list = rules.data ?? []
  const current = editing && editing !== 'new' ? list.find((r) => r.id === editing) : undefined

  return (
    <section>
      <h1 className={layer.sectionTitle}>Автомод</h1>
      <p className={layer.text}>Правила проверяют сообщения при отправке и правке. Администраторы сервера под проверку не попадают, для остальных можно задать роли и каналы-исключения.</p>
      {editing ? (
        <RuleEditor guild={guild} rule={current} onDone={() => { setEditing(null); invalidate() }} onCancel={() => setEditing(null)} />
      ) : (
        <>
          <div className={layer.buttonRow}>
            <button type="button" className={layer.button} onClick={() => setEditing('new')}><Plus size={16} /> Создать правило</button>
          </div>
          {rules.isPending ? <p className={layer.text}>Загружаем…</p> : null}
          {rules.isError ? <p className={gs.error}>{describeError(rules.error).message}</p> : null}
          {!rules.isPending && list.length === 0 ? <p className={layer.text}>Правил пока нет. Начните со встроенных списков или лимита упоминаний.</p> : null}
          <ul className={gs.table}>
            {list.map((r) => (
              <li key={r.id} className={cx(gs.tableRow, !r.enabled && styles.rowOff)}>
                <ShieldCheck size={20} className={styles.icon} />
                <div className={gs.tableMain}>
                  <span className={gs.memberName}>{r.name}</span>
                  <span className={gs.tableMeta}>{triggerLabel(r.trigger_type)} · {actionsLabel(r)}{r.exempt_roles.length ? ` · исключений: ${r.exempt_roles.length + r.exempt_channels.length}` : ''}</span>
                </div>
                <label className={gs.switch} data-tooltip={r.enabled ? 'Выключить' : 'Включить'}>
                  <input type="checkbox" checked={r.enabled} disabled={toggle.isPending} onChange={() => toggle.mutate(r)} aria-label={`Правило ${r.name} включено`} />
                </label>
                <button type="button" className={gs.smallButton} onClick={() => setEditing(r.id)}>Изменить</button>
                <button type="button" className={cx(gs.smallButton, gs.smallDanger)} disabled={remove.isPending} onClick={() => remove.mutate(r.id)} aria-label={`Удалить правило ${r.name}`}><Trash2 size={16} /></button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

function RuleEditor({ guild, rule, onDone, onCancel }: { guild: Guild; rule: AutomodRule | undefined; onDone: () => void; onCancel: () => void }) {
  const channels = useGuildStore((s) => s.channels[guild.id])
  const textChannels = useMemo(() => Object.values(channels ?? {}).filter((c) => c.type === ChannelType.GuildText).sort((a, b) => (a.position ?? 0) - (b.position ?? 0)), [channels])
  const roles = useMemo(() => [...guild.roles].filter((r) => r.id !== guild.id).sort((a, b) => b.position - a.position), [guild])
  const [d, setD] = useState<Draft>(() => (rule ? toDraft(rule) : emptyDraft))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<Record<string, string>>({})
  const patch = (p: Partial<Draft>) => setD((x) => ({ ...x, ...p }))
  const toggleId = (key: 'exemptRoles' | 'exemptChannels' | 'presets', id: string | number) =>
    setD((x) => {
      const list = x[key] as (string | number)[]
      const next = list.includes(id) ? list.filter((v) => v !== id) : [...list, id]
      return { ...x, [key]: next } as Draft
    })

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setFields({})
    try {
      const body = toInput(d)
      if (body.actions?.length === 0) throw new Error('Выберите хотя бы одно действие')
      if (rule) await guildApi.updateAutomodRule(guild.id, rule.id, body)
      else await guildApi.createAutomodRule(guild.id, body)
      onDone()
    } catch (err) {
      if (err instanceof Error && !('status' in err)) setError(err.message)
      else {
        const desc = describeError(err)
        setFields(desc.fields)
        setError(desc.message)
      }
    } finally {
      setBusy(false)
    }
  }

  const isKeyword = d.trigger === AutomodTrigger.Keyword
  const isPreset = d.trigger === AutomodTrigger.KeywordPreset
  const fieldError = (name: string) => fields[name] ?? Object.entries(fields).find(([k]) => k.startsWith(name))?.[1]

  return (
    <form className={auth.form} onSubmit={submit} noValidate>
      <h2 className={layer.subTitle}>{rule ? `Правило «${rule.name}»` : 'Новое правило'}</h2>
      <Field label="Название" htmlFor="am-name" error={fieldError('name')}>
        <input id="am-name" className={auth.input} value={d.name} maxLength={100} onChange={(e) => patch({ name: e.target.value })} placeholder="Например: без спойлеров" />
      </Field>
      <div className={styles.group} role="radiogroup" aria-label="Тип правила">
        {TRIGGERS.map((t) => (
          <label key={t.value} className={cx(styles.option, d.trigger === t.value && styles.optionActive)}>
            <input type="radio" name="am-trigger" value={t.value} checked={d.trigger === t.value} disabled={Boolean(rule)} onChange={() => patch({ trigger: t.value })} />
            <span className={styles.optionText}>
              <span className={styles.optionLabel}>{t.label}</span>
              <span className={styles.optionHint}>{t.hint}</span>
            </span>
          </label>
        ))}
      </div>
      {isKeyword ? (
        <>
          <Field label="Ключевые слова" htmlFor="am-keywords" hint="По одному на строку или через запятую. слово — целиком, слово* — начало, *слово — конец, *слово* — где угодно" error={fieldError('trigger_metadata.keyword_filter') ?? fieldError('trigger_metadata')}>
            <textarea id="am-keywords" className={cx(auth.input, styles.textarea)} value={d.keywords} rows={4} onChange={(e) => patch({ keywords: e.target.value })} />
          </Field>
          <Field label="Регулярные выражения (RE2)" htmlFor="am-regex" hint="До 10, по одному на строку; \b работает только для латиницы" error={fieldError('trigger_metadata.regex_patterns')}>
            <textarea id="am-regex" className={cx(auth.input, styles.textarea)} value={d.regex} rows={2} onChange={(e) => patch({ regex: e.target.value })} />
          </Field>
          <div className={styles.checks}>
            <label className={auth.checkbox}><input type="checkbox" checked={d.links} onChange={(e) => patch({ links: e.target.checked })} /> Ссылки (http и https)</label>
            <label className={auth.checkbox}><input type="checkbox" checked={d.invites} onChange={(e) => patch({ invites: e.target.checked })} /> Приглашения на серверы</label>
          </div>
        </>
      ) : null}
      {isPreset ? (
        <div className={styles.checks} role="group" aria-label="Встроенные списки">
          {PRESETS.map((p) => (
            <label key={p.value} className={auth.checkbox}><input type="checkbox" checked={d.presets.includes(p.value)} onChange={() => toggleId('presets', p.value)} /> {p.label}</label>
          ))}
        </div>
      ) : null}
      {isKeyword || isPreset ? (
        <Field label="Исключения" htmlFor="am-allow" hint="Слова и фразы, которые не считаются нарушением (тот же синтаксис)" error={fieldError('trigger_metadata.allow_list')}>
          <textarea id="am-allow" className={cx(auth.input, styles.textarea)} value={d.allow} rows={2} onChange={(e) => patch({ allow: e.target.value })} />
        </Field>
      ) : null}
      {d.trigger === AutomodTrigger.MentionSpam ? (
        <Field label="Максимум упоминаний в сообщении" htmlFor="am-mentions" error={fieldError('trigger_metadata.mention_total_limit')}>
          <input id="am-mentions" type="number" min={1} max={50} className={auth.input} value={d.mentionLimit} onChange={(e) => patch({ mentionLimit: Number(e.target.value) })} />
        </Field>
      ) : null}

      <h3 className={styles.h3}>Действия</h3>
      <div className={styles.checks}>
        <label className={auth.checkbox}><input type="checkbox" checked={d.block} onChange={(e) => patch({ block: e.target.checked })} /> Заблокировать сообщение</label>
        {d.block ? (
          <Field label="Что показать автору" htmlFor="am-custom" hint="До 150 символов; пусто — стандартный текст" error={fieldError('actions.custom_message')}>
            <input id="am-custom" className={auth.input} value={d.customMessage} maxLength={150} onChange={(e) => patch({ customMessage: e.target.value })} />
          </Field>
        ) : null}
        <label className={auth.checkbox}><input type="checkbox" checked={d.alert} onChange={(e) => patch({ alert: e.target.checked })} /> Оповестить модераторов в канале</label>
        {d.alert ? (
          <Field label="Канал оповещений" htmlFor="am-alert" error={fieldError('actions.channel_id')}>
            <Select id="am-alert" value={d.alertChannel} options={[{ value: '', label: 'Выберите канал' }, ...textChannels.map((c) => ({ value: c.id, label: `#${c.name}` }))]} onChange={(v) => patch({ alertChannel: v })} />
          </Field>
        ) : null}
        {d.trigger !== AutomodTrigger.Spam ? (
          <>
            <label className={auth.checkbox}><input type="checkbox" checked={d.timeout} onChange={(e) => patch({ timeout: e.target.checked })} /> Выдать таймаут</label>
            {d.timeout ? (
              <Field label="Длительность таймаута" htmlFor="am-timeout" error={fieldError('actions.duration_seconds')}>
                <Select id="am-timeout" value={d.timeoutSeconds} options={TIMEOUTS} onChange={(v) => patch({ timeoutSeconds: v })} />
              </Field>
            ) : null}
          </>
        ) : null}
      </div>

      <h3 className={styles.h3}>Исключения</h3>
      {roles.length ? (
        <div className={styles.chips} role="group" aria-label="Роли-исключения">
          {roles.map((r) => (
            <label key={r.id} className={cx(styles.chip, d.exemptRoles.includes(r.id) && styles.chipActive)}>
              <input type="checkbox" checked={d.exemptRoles.includes(r.id)} onChange={() => toggleId('exemptRoles', r.id)} />
              <span className={styles.chipDot} style={{ background: r.color ? `#${r.color.toString(16).padStart(6, '0')}` : 'var(--text-muted)' }} />{r.name}
            </label>
          ))}
        </div>
      ) : <p className={layer.text}>Ролей кроме @everyone пока нет.</p>}
      <div className={styles.chips} role="group" aria-label="Каналы-исключения">
        {textChannels.map((c) => (
          <label key={c.id} className={cx(styles.chip, d.exemptChannels.includes(c.id) && styles.chipActive)}>
            <input type="checkbox" checked={d.exemptChannels.includes(c.id)} onChange={() => toggleId('exemptChannels', c.id)} />#{c.name}
          </label>
        ))}
      </div>
      <label className={auth.checkbox}><input type="checkbox" checked={d.enabled} onChange={(e) => patch({ enabled: e.target.checked })} /> Правило включено</label>
      {error ? <p className={gs.error} role="alert">{error}</p> : null}
      <div className={layer.buttonRow}>
        <button type="submit" className={layer.button} disabled={busy}>{busy ? 'Сохраняем…' : rule ? 'Сохранить' : 'Создать'}</button>
        <button type="button" className={cx(layer.button, layer.buttonSecondary)} onClick={onCancel} disabled={busy}>Отмена</button>
      </div>
    </form>
  )
}
